import { Link } from 'react-router-dom';
import {
  UXP_CS_ID,
  COMMAND_CENTER_METRICS,
  ONBOARDING_STEPS,
  ADOPTION_METRICS,
  GUIDANCE_FEATURES,
  COACH_CAPABILITIES,
  SUCCESS_MILESTONES,
  DEMO_FEATURES,
  HEALTH_FACTORS,
  SUCCESS_PLAYBOOKS,
  PRODUCTION_READINESS_CHECKS,
  FEEDBACK_TYPES,
  SUCCESS_ANALYTICS,
  COLLABORATION_FEATURES,
  ACCESSIBILITY_STANDARDS,
  ARCHITECTURE_STAGES,
  LIVE_ROUTES,
  DEMO_KPIS,
  DEMO_ONBOARDING,
  DEMO_ADOPTION,
  DEMO_GUIDANCE,
  DEMO_COACH,
  DEMO_MILESTONES,
  DEMO_SANDBOX,
  DEMO_READINESS,
  buildCustomerSuccessDashboard,
} from '../../customersuccess/framework';
import {
  CustomerSuccessCommandCenterPanel,
  BusinessOnboardingPanel,
  ProductAdoptionPanel,
  InteractiveGuidancePanel,
  AISuccessCoachPanel,
  SuccessMilestonesPanel,
  DemoSandboxPanel,
  ProductionReadinessPanel,
} from '../../components/customersuccess';
import { Card, KpiCard } from '../../components/ui';

export function CustomerSuccessPage() {
  const dashboard = buildCustomerSuccessDashboard();

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">{UXP_CS_ID}</p>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Customer Success Platform</h1>
        <p className="mt-1 text-sm text-slate-500">
          Customer Success, Digital Adoption, Demo Platform & Production Readiness Operating System —
          guide every customer from registration to measurable business value.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(LIVE_ROUTES).map(([key, path]) => (
            <Link
              key={key}
              to={path}
              className="rounded-lg border border-teal-200 bg-teal-50 px-3 py-1.5 text-xs font-medium text-teal-700 hover:bg-teal-100 dark:border-teal-900 dark:bg-teal-950 dark:text-teal-300"
            >
              {path}
            </Link>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Command Center Metrics" value={dashboard.command_center_metrics.toString()} />
        <KpiCard label="Onboarding Steps" value={dashboard.onboarding_steps.toString()} />
        <KpiCard label="Success Milestones" value={dashboard.success_milestones.toString()} />
        <KpiCard label="Readiness Checks" value={dashboard.readiness_checks.toString()} />
      </div>

      <section>
        <h2 className="text-lg font-semibold">Architecture Pipeline</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {ARCHITECTURE_STAGES.map((s, i) => (
            <span key={s} className="flex items-center gap-1 text-xs">
              <span className="rounded-lg border border-slate-200 px-2 py-1 dark:border-slate-700">{s}</span>
              {i < ARCHITECTURE_STAGES.length - 1 && <span className="text-slate-400">→</span>}
            </span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Customer Success Command Center</h2>
        <p className="mt-1 text-sm text-slate-500">{COMMAND_CENTER_METRICS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <CustomerSuccessCommandCenterPanel kpis={DEMO_KPIS} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Business Onboarding</h2>
        <p className="mt-1 text-sm text-slate-500">{ONBOARDING_STEPS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <BusinessOnboardingPanel steps={DEMO_ONBOARDING} />
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Product Adoption</h2>
          <p className="mt-1 text-sm text-slate-500">{ADOPTION_METRICS.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <ProductAdoptionPanel metrics={DEMO_ADOPTION} />
          </Card>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Interactive Guidance</h2>
          <p className="mt-1 text-sm text-slate-500">{GUIDANCE_FEATURES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <InteractiveGuidancePanel items={DEMO_GUIDANCE} />
          </Card>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">AI Success Coach</h2>
        <p className="mt-1 text-sm text-slate-500">{COACH_CAPABILITIES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <AISuccessCoachPanel sessions={DEMO_COACH} />
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Success Milestones</h2>
          <p className="mt-1 text-sm text-slate-500">{SUCCESS_MILESTONES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <SuccessMilestonesPanel milestones={DEMO_MILESTONES} />
          </Card>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Demo & Sandbox</h2>
          <p className="mt-1 text-sm text-slate-500">{DEMO_FEATURES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <DemoSandboxPanel environments={DEMO_SANDBOX} />
          </Card>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Production Readiness</h2>
        <p className="mt-1 text-sm text-slate-500">{PRODUCTION_READINESS_CHECKS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <ProductionReadinessPanel checks={DEMO_READINESS} />
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-3">
        <div>
          <h2 className="text-lg font-semibold">Customer Health Engine</h2>
          <p className="mt-2 text-xs text-slate-500">{HEALTH_FACTORS.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Success Playbooks</h2>
          <p className="mt-2 text-xs text-slate-500">{SUCCESS_PLAYBOOKS.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Feedback</h2>
          <p className="mt-2 text-xs text-slate-500">{FEEDBACK_TYPES.join(' · ')}</p>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-3">
        <div>
          <h2 className="text-lg font-semibold">Analytics</h2>
          <p className="mt-2 text-xs text-slate-500">{SUCCESS_ANALYTICS.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Community</h2>
          <p className="mt-2 text-xs text-slate-500">{COLLABORATION_FEATURES.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Accessibility</h2>
          <div className="mt-2 flex flex-wrap gap-1">
            {ACCESSIBILITY_STANDARDS.map((s) => (
              <span key={s} className="rounded border border-slate-200 px-2 py-0.5 text-xs dark:border-slate-700">{s}</span>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
